/** @jsxImportSource @compiled/react */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { Card } from '@atlaskit/smart-card/card/lazy';
import { TitleBlock } from '@atlaskit/smart-card/flexible/title-block';
import {
	ElementName,
	SmartLinkDirection,
	SmartLinkPosition,
	SmartLinkSize,
	CardAction,
} from '@atlaskit/smart-card/enums';
import type { ElementItem } from '@atlaskit/smart-card/flexible/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { token } from '@atlaskit/tokens';
import IconButton from '@atlaskit/button/icon/button';
import IconTile from '@atlaskit/icon/icon-tile';
import type { IconTileProps } from '@atlaskit/icon/types';
import Link from '@atlaskit/link/link';
import { HoverCard } from '@atlaskit/smart-card/hover';
import PinIcon from '@atlaskit/icon/core/pin';
import PinFilledIcon from '@atlaskit/icon/core/pin-filled';
import CrossIcon from '@atlaskit/icon/core/cross';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useSmartLinkContext } from '@atlaskit/link-provider/context';

type LinkData = {
	id: string;
	url: string;
	title: string;
	subtitle?: string;
	isPinned: boolean;
	icon?: IconTileProps['icon'];
	iconAppearance?: IconTileProps['appearance'];
};

type LinkItemProps = {
	link: LinkData;
	onRemove: (linkId: string) => void;
	onPin: (linkId: string) => void;
	isPinned: boolean;
	docPosition: number;
	spaceKey: string;
	spaceId: string;
};

const i18n = defineMessages({
	pin: {
		id: 'space-overview.smart-space-overview.link-item.pin',
		defaultMessage: 'Pin',
		description: 'Accessible label for the pin button on a link item.',
	},
	unpin: {
		id: 'space-overview.smart-space-overview.link-item.unpin',
		defaultMessage: 'Unpin',
		description: 'Accessible label for the unpin button on a link item.',
	},
	remove: {
		id: 'space-overview.smart-space-overview.link-item.remove',
		defaultMessage: 'Remove',
		description: 'Accessible label for the remove button on a link item.',
	},
});

/* Compiled css for the SmartCard wrapper — allows child selectors for internal element overrides */
const smartCardWrapperStyle = css({
	flex: 1,
	minWidth: 0,
	/* SmartCard block: remove default padding so our row padding controls spacing */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& [data-smart-block="true"]': {
		padding: 0,
		gap: token('space.100'),
	},
	/* Remove gap between title and subtitle */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& [data-smart-element-group]': {
		gap: 0,
	},
	/* Icon: wrap in a neutral background box to match CardItem style */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& [data-smart-element-icon="true"]': {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.large'),
		width: '40px',
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		flexShrink: 0,
	},
	/* Title link — a[href] specificity beats SmartCard's class-based link color */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& a[href]': {
		color: token('color.text'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		textDecoration: 'none',
	},
});

/* Variant used when an IconTile is provided — hides SmartCard's own icon element */
const smartCardWrapperStyleWithTile = css({
	flex: 1,
	minWidth: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& [data-smart-block="true"]': {
		padding: 0,
		gap: token('space.100'),
	},
	/* Remove gap between title and subtitle */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& [data-smart-element-group]': {
		gap: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& [data-smart-element-icon="true"]': {
		display: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& a[href]': {
		color: token('color.text'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		textDecoration: 'none',
	},
});

/* Icon column: Smart Card’s layered link only covers the Card; DS Link opens the URL in a new tab */
const iconColumnWrapperStyles = css({
	display: 'flex',
	alignItems: 'center',
	flexShrink: 0,
});

const hoverCardContainerStyles = css({
	flex: 1,
	minWidth: 0,
});

const hoverCardTriggerStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	width: '100%',
	minWidth: 0,
});

const itemStyles = cssMap({
	outerWrapper: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
	},
	row: {
		paddingBlock: token('space.075'),
		paddingInline: token('space.100'),
		borderRadius: token('radius.large'),
		cursor: 'pointer',
		flex: 1,
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:focus-within': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	actions: {
		flexShrink: 0,
		display: 'none',
		alignItems: 'center',
		gap: token('space.025'),
		opacity: 0,
		transition: 'opacity 150ms ease',
	},
	actionsVisible: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		gap: token('space.025'),
		opacity: 1,
		transition: 'opacity 150ms ease',
	},
});

const cardUiProps = {
	hideBackground: true,
	hideElevation: true,
	hidePadding: true,
	/* TitleBlock anchorTarget is applied to the full-area LayeredLink <a> when this is true */
	clickableContainer: true,
	size: SmartLinkSize.Medium,
	hideActions: true,
};

const titleBlockSubtitle: ElementItem[] = [{ name: ElementName.CreatedBy }];

const hoverCardActionOptions = {
	hide: false as const,
	exclude: [
		CardAction.AutomationAction,
		CardAction.ChangeStatusAction,
		CardAction.CopyLinkAction,
		CardAction.DownloadAction,
		CardAction.FollowAction,
		CardAction.PreviewAction,
		CardAction.RovoChatAction,
		CardAction.ViewAction,
	],
};

export const LinkItem = ({
	link,
	onRemove,
	onPin,
	isPinned = false,
	docPosition,
	spaceKey,
	spaceId,
}: LinkItemProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const pinnedAtRef = useRef<number | null>(null);

	// Provide Top Docs attribution context to analytics events fired inside the
	// HoverCard popup (e.g. `smartLink clicked titleGoToLink`). Events fired
	// inside will carry this data in their context array, making hover preview
	// clicks attributable to the SmartSpaceOverview Top Docs module.
	const hoverCardAnalyticsContext = useMemo(
		() => ({
			attributes: {
				originSpaceKey: spaceKey,
				originSpaceId: spaceId,
				spaceOverviewTopDocsPosition: docPosition,
			},
		}),
		[docPosition, spaceKey, spaceId],
	);

	const handleMouseEnter = useCallback(() => setIsHovered(true), []);
	const handleMouseLeave = useCallback(() => setIsHovered(false), []);
	const handleFocus = useCallback(() => setIsHovered(true), []);
	const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
		if (!e.currentTarget.contains(e.relatedTarget)) {
			setIsHovered(false);
		}
	}, []);

	const { store } = useSmartLinkContext();
	const getContentType = useCallback(() => {
		const cardState = store.getState()[link.url];
		const data = cardState?.details?.data;
		const generator = data && 'generator' in data ? data.generator : undefined;
		const platform =
			generator &&
			typeof generator === 'object' &&
			'@type' in generator &&
			generator['@type'] !== 'Link'
				? (generator as { name?: string }).name
				: undefined;
		return {
			platform,
			contentType: cardState?.details?.meta?.resourceType,
		};
	}, [store, link.url]);

	const handlePin = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			e.stopPropagation();
			e.nativeEvent.stopImmediatePropagation();

			if (isPinned) {
				const pinnedDurationMs =
					pinnedAtRef.current !== null ? Date.now() - pinnedAtRef.current : undefined;
				pinnedAtRef.current = null;
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'unpinButton',
						source: 'smartSpaceOverview',
						attributes: {
							moduleType: 'topDocs',
							docId: link.id,
							contentType: getContentType(),
							pinnedDurationMs,
							spaceKey,
						},
					},
				}).fire();
			} else {
				pinnedAtRef.current = Date.now();
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'pinButton',
						source: 'smartSpaceOverview',
						attributes: {
							moduleType: 'topDocs',
							docId: link.id,
							contentType: getContentType(),
							previousPosition: docPosition,
							spaceKey,
						},
					},
				}).fire();
			}
			onPin?.(link.id);
		},
		[createAnalyticsEvent, getContentType, onPin, isPinned, link.id, docPosition, spaceKey],
	);

	const handleRemove = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			e.stopPropagation();
			e.nativeEvent.stopImmediatePropagation();
			onRemove?.(link.id);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'removeDocButton',
					source: 'smartSpaceOverview',
					attributes: {
						moduleType: 'topDocs',
						docId: link.id,
						contentType: getContentType(),
						docPosition,
						spaceKey,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, getContentType, onRemove, link.id, docPosition, spaceKey],
	);

	const handleLinkClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'card',
				actionSubjectId: 'topDoc',
				source: 'smartSpaceOverview',
				attributes: {
					moduleType: 'topDocs',
					docId: link.id,
					contentType: getContentType(),
					docPosition,
					spaceKey,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, getContentType, link.id, docPosition, spaceKey]);

	return (
		<Box
			testId="link-item"
			xcss={itemStyles.outerWrapper}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onFocus={handleFocus}
			onBlur={handleBlur}
		>
			<Flex
				xcss={itemStyles.row}
				justifyContent="space-between"
				alignItems="center"
				gap="space.100"
			>
				<div css={hoverCardContainerStyles}>
					<AnalyticsContext data={hoverCardAnalyticsContext}>
						<HoverCard url={link.url} actionOptions={hoverCardActionOptions}>
							<div css={hoverCardTriggerStyles}>
								{link.icon && (
									<div css={iconColumnWrapperStyles}>
										<Link
											href={link.url}
											target="_blank"
											rel="noopener noreferrer"
											appearance="subtle"
										>
											<IconTile
												icon={link.icon}
												label={link.title}
												appearance={link.iconAppearance ?? 'gray'}
												size="large"
											/>
										</Link>
									</div>
								)}
								{link.icon ? (
									<div css={smartCardWrapperStyleWithTile}>
										<Card
											appearance="inline"
											url={link.url}
											placeholder={link.title}
											disablePreviewPanel
											ui={cardUiProps}
											onClick={handleLinkClick}
										>
											<TitleBlock
												direction={SmartLinkDirection.Horizontal}
												maxLines={1}
												position={SmartLinkPosition.Center}
												text={link.title}
												anchorTarget="_blank"
												hideTitleTooltip
												subtitle={titleBlockSubtitle}
											/>
										</Card>
									</div>
								) : (
									<div css={smartCardWrapperStyle}>
										<Card
											appearance="inline"
											url={link.url}
											placeholder={link.title}
											disablePreviewPanel
											ui={cardUiProps}
											onClick={handleLinkClick}
										>
											<TitleBlock
												direction={SmartLinkDirection.Horizontal}
												maxLines={1}
												position={SmartLinkPosition.Center}
												text={link.title}
												anchorTarget="_blank"
												hideTitleTooltip
												subtitle={titleBlockSubtitle}
											/>
										</Card>
									</div>
								)}
							</div>
						</HoverCard>
					</AnalyticsContext>
				</div>
				<Box xcss={isPinned || isHovered ? itemStyles.actionsVisible : itemStyles.actions}>
					<IconButton
						icon={isPinned ? PinFilledIcon : PinIcon}
						label={isPinned ? formatMessage(i18n.unpin) : formatMessage(i18n.pin)}
						appearance="subtle"
						spacing="compact"
						onClick={handlePin}
					/>
					{!isPinned && (
						<IconButton
							icon={CrossIcon}
							label={formatMessage(i18n.remove)}
							appearance="subtle"
							spacing="compact"
							onClick={handleRemove}
						/>
					)}
				</Box>
			</Flex>
		</Box>
	);
};

export type { LinkData };
